import React from 'react';
import PropTypes from 'prop-types';
import styles from './AccessCodeOnlyForm.module.scss';
import shoobMark from '../../assets/shoob-mark.png';

// Local, page-scoped strings, matching the pattern in AccessCodeOnlyForm and
// GetAccessCode -- retire when the app grows real i18n (react-i18next).
// Until this existed, a parent who filled in the Spanish form landed on an
// English-only affirmation.
//
// NOTE: the consent sentence is a legal affirmation and BOTH languages need a
// sign-off from someone who can approve legal copy before this reaches
// customers. The English was shortened from the original 39-word version; it
// keeps all six substantive elements (consent trigger, who is affirming, the
// guardian/close-relative claim, which student, the print-and-mail
// authorization, and the destination address), but two things did change:
//   - "the student whose image(s) they are requesting" -> "this student",
//     which is scoped by the name rendered directly above it.
//   - "their home address" -> "your home address", resolving an ambiguity
//     about whose address was meant.
//   - "you affirm" -> "you confirm" (and "usted afirma" -> "usted confirma"),
//     plainer language for the same undertaking.
// The Spanish mirrors the shortened English.
const STRINGS = {
  en: {
    continuingFor: 'Continuing for',
    consent: 'By continuing, you confirm that you are this student’s legal guardian or close relative, and that you authorize Shoob Photography to print and mail their portraits to your home address.',
    continue: 'Continue',
    wrongStudentPrefix: 'Not the right student? ',
    wrongStudentLink: 'Try a different code →',
  },
  es: {
    continuingFor: 'Continuando para',
    consent: 'Al continuar, usted confirma que es el tutor legal o pariente cercano de este estudiante, y que autoriza a Shoob Photography a imprimir y enviar sus retratos por correo a su domicilio.',
    continue: 'Continuar',
    wrongStudentPrefix: '¿No es el estudiante correcto? ',
    wrongStudentLink: 'Pruebe otro código →',
  },
};

// This screen is step 2 of 2: the parent has just typed their access code and
// is being asked to confirm the student and continue. It is laid out to sit
// EXACTLY on top of the entry screen's card -- same width, same padding, same
// card height, same Continue button size and vertical position -- so a thumb
// resting on Continue does not have to move between the two taps. See the
// height notes on .affirmationText in AccessCodeOnlyForm.module.scss.
export default function AffirmationText({
  student,
  handleContinue,
  handleBack,
  lang,
}) {
  const t = STRINGS[lang === 'es' ? 'es' : 'en'];

  return (
    <div className={styles.affirmationText}>
      {/* Same brand mark, same size, same position as the entry card's, so
          the top of the card doesn't visibly change between the two steps.
          Decorative -- the site header already announces the brand. */}
      <div className={styles.logoBadge} aria-hidden="true">
        <img src={shoobMark} alt="" className={styles.logoMark} />
      </div>
      {/* The student's name is the one thing a parent must actually check, so
          it leads: a small muted label above, the name large below. Previously
          the name was the muted line and the legal paragraph dominated.
          Wrapped as one block so it can be centred as a unit in the space
          between the brand mark and the acknowledgement panel. */}
      <div className={styles.affirmationHeading}>
        <p className={styles.affirmationEyebrow}>{t.continuingFor}</p>
        <p className={styles.affirmationName}>
          {/* Decorative reinforcement that the code matched a student. The
              name beside it already carries that meaning in text, so the icon
              is hidden from screen readers rather than announced as "check". */}
          <span className={styles.affirmationCheck} aria-hidden="true">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </span>
          {/* Wrapped so the name is a single flex item -- bare text beside the
              icon would rely on anonymous flex-item generation to stay whole. */}
          <span>
            {student.firstName}
            {' '}
            {student.lastName}
          </span>
        </p>
      </div>
      {/* The legal affirmation sits in its own tinted panel so it reads as an
          acknowledgement block to skim rather than as the page's message. */}
      <p className={styles.affirmationConsent}>{t.consent}</p>
      {/* Plain button on the shared .cta class -- the same element the entry
          form's Continue uses, so the two are identical in height, radius,
          colour and font weight. */}
      <button type="button" className={styles.cta} onClick={handleContinue}>
        {t.continue}
      </button>
      {/* Occupies the same slot as the entry card's "Don't have an access
          code?" row, which is what keeps Continue at the same distance from
          the bottom of the card. It is also the only way back if the name
          shown isn't the student the parent expected. */}
      {handleBack && (
        <p className={styles.secondaryRow}>
          {t.wrongStudentPrefix}
          <button type="button" className={styles.linkButton} onClick={handleBack}>
            {t.wrongStudentLink}
          </button>
        </p>
      )}
    </div>
  );
}

AffirmationText.propTypes = {
  student: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
  }).isRequired,
  handleContinue: PropTypes.func.isRequired,
  // Returns to the entry form with the typed values intact. Optional: the
  // "not the right student?" row is simply omitted when it isn't provided.
  handleBack: PropTypes.func,
  // Anything other than 'es' falls back to English.
  lang: PropTypes.string,
};

AffirmationText.defaultProps = {
  handleBack: undefined,
  lang: 'en',
};
